import cs from "classnames";
import React from "react";

import "./Header.css";

interface HeaderProps {
	module?: string;
	variant: string;
	status: React.ReactNode;
	title: React.ReactNode;
	actions: React.ReactNode;
	values: React.ReactNode;
}

export const Header: React.FunctionComponent<HeaderProps> = ({
	module,
	variant,
	status,
	actions,
	title,
	values
}) => {
	const ref = React.useRef<HTMLDivElement | null>(null);

	const [width, setWidth] = React.useState(0);
	const isMobile = React.useMemo(() => width < 500, [width]);

	React.useEffect(() => {
		const { current: el } = ref;
		if (el !== null) {
			const observer = new ResizeObserver(() => {
				const width = el.offsetWidth - (el.offsetWidth % 100);
				if (width >= 1000) {
					return setWidth(1000);
				}
				if (width >= 700) {
					return setWidth(700);
				}
				if (width >= 600) {
					return setWidth(600);
				}
				if (width >= 500) {
					return setWidth(500);
				}
				setWidth(400);
			});
			observer.observe(el);
			return () => {
				observer.disconnect();
			};
		}
	});

	return (
		<div
			ref={ref}
			className={cs(
				"dashboard-header",
				module === undefined
					? undefined
					: `dashboard-header--${module}`,
				`dashboard-header--${variant}`,
				`dashboard-header--${width}`,
				{
					"dashboard-header--mobile": isMobile,
					"dashboard-header--desktop": !isMobile
				}
			)}
		>
			{isMobile ? (
				<>
					<div className="dashboard-header__top">
						<div className="dashboard-header__left">{status}</div>
						<div className="dashboard-header__right">
							<div className="dashboard-header__title">
								{title}
							</div>
							<div className="dashboard-header__actions">
								{actions}
							</div>
						</div>
					</div>
					<div className="dashboard-header__bottom">
						<div className="dashboard-header__values">{values}</div>
					</div>
				</>
			) : (
				<>
					<div className="dashboard-header__left">{status}</div>
					<div className="dashboard-header__right">
						<div className="dashboard-header__top">
							<div className="dashboard-header__title">
								{title}
							</div>
							<div className="dashboard-header__actions">
								{actions}
							</div>
						</div>
						<div className="dashboard-header__bottom">
							<div className="dashboard-header__values">
								{values}
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

type HeaderStatusProps = Pick<
	React.HTMLProps<HTMLDivElement>,
	"title" | "onClick"
> & {
	progress: number;
};

export const HeaderStatus: React.FunctionComponent<
	React.PropsWithChildren<HeaderStatusProps>
> = ({ title, onClick, progress, children }) => (
	<div className="dashboard-header__status" title={title} onClick={onClick}>
		<div className="dashboard-header__progress">
			<div className="dashboard-header__progress__bar">
				{progress > 0 && (
					<div className="dashboard-header__progress__segment" />
				)}
				{progress > 1 && (
					<div className="dashboard-header__progress__segment" />
				)}
				{progress > 2 && (
					<div className="dashboard-header__progress__segment" />
				)}
				{progress > 3 && (
					<div className="dashboard-header__progress__segment" />
				)}
				{progress > 4 && (
					<div className="dashboard-header__progress__segment" />
				)}
			</div>
			<div className="dashboard-header__progress__mask">
				<div className="dashboard-header__progress__background"></div>
			</div>
		</div>
		<div className="dashboard-header__status__content">{children}</div>
	</div>
);

interface HeaderValueProps {
	variant: string;
	onMouseOver?(event: React.MouseEvent): unknown;
	onMouseLeave?(event: React.MouseEvent): unknown;
}

export const HeaderValue: React.FunctionComponent<
	React.PropsWithChildren<HeaderValueProps>
> = ({ variant, children, ...props }) => (
	<div
		{...props}
		className={`dashboard-header__value dashboard-header__value--${variant}`}
	>
		{children}
	</div>
);

export const HeaderAlert: React.FunctionComponent = () => (
	<div className="dashboard-header__alert">!</div>
);

interface HeaderWarningProps {
	title?: string;
}

export const HeaderWraning: React.FunctionComponent<
	React.PropsWithChildren<HeaderWarningProps>
> = ({ title, children }) => (
	<div className="dashboard-header__warning" title={title}>
		{children}
	</div>
);
